import { jsx, Fragment, jsxs } from '@emotion/react/jsx-runtime';
import { useMemo, Component, createContext, useState, Fragment as Fragment$1, useContext, useEffect } from 'react';
import { createAPIClient } from '@monite/sdk-react';
import { createSecureRequestFn, QraftSecureRequestFn } from '@openapi-qraft/react/Unstable_QraftSecureRequestFn';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { List, ListItemButton, ListItemIcon, ListItemText, Collapse, Box, Drawer, Avatar, Typography, CircularProgress, Stack } from '@mui/material';
import { MonetizationOn, DoneOutline, Receipt, AccountBalance, Tab, Settings, Badge, Label, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useLingui, I18nProvider } from '@lingui/react';
import { setupI18n } from '@lingui/core';

const EntityIdLoader = ({ children, fetchToken, apiUrl })=>{
    return /*#__PURE__*/ jsx(EntityIdLoaderProvider, {
        fetchToken: fetchToken,
        apiUrl: apiUrl,
        children: /*#__PURE__*/ jsx(ErrorBoundary, {
            children: /*#__PURE__*/ jsx(EntityIdLoaderRenderCallback, {
                children: children
            })
        })
    });
};
const EntityIdLoaderProvider = ({ children, apiUrl, fetchToken })=>{
    const requestFn = useMemo(()=>createEntityUsersMyEntityRequestFn(fetchToken), [
        fetchToken
    ]);
    return /*#__PURE__*/ jsx(EntityIdContext.Provider, {
        value: {
            requestFn,
            baseUrl: apiUrl
        },
        children: children
    });
};
const EntityIdLoaderRenderCallback = ({ children })=>{
    const { api } = createAPIClient({
        context: EntityIdContext
    });
    const getEntityUsersMeQuery = api.entityUsers.getEntityUsersMyEntity.useSuspenseQuery({}, {
        // Keep entity user data in cache indefinitely,
        // as it's unlikely to change during iframe app lifecycle.
        staleTime: Infinity,
        gcTime: Infinity
    });
    return /*#__PURE__*/ jsx(Fragment, {
        children: children(getEntityUsersMeQuery.data.id)
    });
};
const createEntityUsersMyEntityRequestFn = (fetchToken)=>{
    const { requestFn: moniteRequestFn } = createAPIClient();
    return createSecureRequestFn({
        async HTTPBearer () {
            const { access_token } = await fetchToken();
            return {
                token: access_token,
                refreshInterval: Infinity
            };
        }
    }, moniteRequestFn, new QueryClient());
};
class ErrorBoundary extends Component {
    constructor(props){
        super(props);
        this.state = {
            isError: false
        };
    }
    static getDerivedStateFromError(error) {
        return {
            isError: true,
            error: error
        };
    }
    render() {
        if (this.state.isError) return null;
        return this.props.children;
    }
}
const EntityIdContext = /*#__PURE__*/ createContext(undefined);

const ROUTES = {
    counterparts: '/counterparts',
    payables: '/payables',
    settings: '/settings',
    approvalPolicies: '/settings/approval-policies',
    approvalRequests: '/approval-requests',
    roles: '/settings/roles',
    tags: '/settings/tags',
    receivables: '/receivables',
    products: '/products',
    onboarding: '/onboarding',
    integrations: '/integrations',
    rolesApprovals: '/settings/roles-approvals'
};

const getNavigationData = (i18n)=>({
        payables: {
            label: i18n._({
                id: "HjWVjB"
            }),
            url: ROUTES.payables,
            renderIcon: (props)=>/*#__PURE__*/ jsx(MonetizationOn, {
                    ...props
                })
        },
        approvalRequests: {
            label: 'Approval Requests',
            url: ROUTES.approvalRequests,
            renderIcon: (props)=>/*#__PURE__*/ jsx(DoneOutline, {
                    ...props
                })
        },
        receivables: {
            label: i18n._({
                id: "mUv9U4"
            }),
            url: ROUTES.receivables,
            renderIcon: (props)=>/*#__PURE__*/ jsx(Receipt, {
                    ...props
                })
        },
        counterparts: {
            label: i18n._({
                id: "dU8ZD6"
            }),
            url: ROUTES.counterparts,
            renderIcon: (props)=>/*#__PURE__*/ jsx(AccountBalance, {
                    ...props
                })
        },
        products: {
            label: i18n._({
                id: "N0qXpE"
            }),
            url: ROUTES.products,
            renderIcon: (props)=>/*#__PURE__*/ jsx(Tab, {
                    ...props
                })
        },
        settings: {
            label: i18n._({
                id: "Tz0i8g"
            }),
            url: ROUTES.settings,
            renderIcon: (props)=>/*#__PURE__*/ jsx(Settings, {
                    ...props
                }),
            children: {
                rolesApprovals: {
                    label: i18n._({
                        id: "McU8nI"
                    }),
                    url: ROUTES.rolesApprovals,
                    renderIcon: (props)=>/*#__PURE__*/ jsx(Badge, {
                            ...props
                        })
                },
                tags: {
                    label: i18n._({
                        id: "OYHzN1"
                    }),
                    url: ROUTES.tags,
                    renderIcon: (props)=>/*#__PURE__*/ jsx(Label, {
                            ...props
                        })
                },
                onboarding: {
                    label: i18n._({
                        id: "Q3KR12"
                    }),
                    url: ROUTES.onboarding,
                    renderIcon: (props)=>/*#__PURE__*/ jsx(Label, {
                            ...props
                        })
                },
                integrations: {
                    label: i18n._({
                        id: "nbfdhU"
                    }),
                    url: ROUTES.integrations,
                    renderIcon: (props)=>/*#__PURE__*/ jsx(Label, {
                            ...props
                        })
                }
            }
        }
    });

const Menu = ()=>{
    const { i18n } = useLingui();
    const navigate = useNavigate();
    const navigationData = getNavigationData(i18n);
    const [openedItem, setOpenedItem] = useState(null);
    const handleCollapse = (key)=>{
        setOpenedItem(key === openedItem ? null : key);
    };
    return /*#__PURE__*/ jsx(List, {
        sx: {
            width: '100%'
        },
        children: Object.keys(navigationData).map((key)=>{
            const item = navigationData[key];
            return /*#__PURE__*/ jsxs(Fragment$1, {
                children: [
                    /*#__PURE__*/ jsxs(ListItemButton, {
                        onClick: ()=>item.children ? handleCollapse(key) : navigate(item.url),
                        children: [
                            /*#__PURE__*/ jsx(ListItemIcon, {
                                children: item.renderIcon({
                                    color: 'primary'
                                })
                            }),
                            /*#__PURE__*/ jsx(ListItemText, {
                                primary: item.label
                            }),
                            item.children && (openedItem === key ? /*#__PURE__*/ jsx(ExpandLess, {}) : /*#__PURE__*/ jsx(ExpandMore, {}))
                        ]
                    }),
                    /*#__PURE__*/ jsx(Collapse, {
                        in: openedItem === key,
                        timeout: "auto",
                        unmountOnExit: true,
                        children: /*#__PURE__*/ jsx(List, {
                            component: "div",
                            disablePadding: true,
                            children: item.children && Object.values(item.children).map((subItem)=>/*#__PURE__*/ jsxs(ListItemButton, {
                                    sx: {
                                        pl: 4
                                    },
                                    onClick: ()=>navigate(subItem.url),
                                    children: [
                                        /*#__PURE__*/ jsx(ListItemIcon, {
                                            children: subItem.renderIcon({
                                                color: 'primary'
                                            })
                                        }),
                                        /*#__PURE__*/ jsx(ListItemText, {
                                            primary: subItem.label
                                        })
                                    ]
                                }, subItem.url))
                        })
                    })
                ]
            }, item.url);
        })
    });
};

const SDKDemoAPIProvider = ({ children, apiUrl, entityId, fetchToken })=>{
    const queryClient = useQueryClient();
    const { requestFn, api, version } = useMemo(()=>createAPIClient({
            context: SDKDemoQraftContext,
            entityId
        }), [
        entityId
    ]);
    return /*#__PURE__*/ jsx(QraftSecureRequestFn, {
        requestFn: requestFn,
        securitySchemes: {
            HTTPBearer: async ()=>{
                const { access_token } = await fetchToken();
                return {
                    token: access_token,
                    refreshInterval: 10 * 60_000
                };
            }
        },
        children: (securedRequestFn)=>/*#__PURE__*/ jsx(SDKDemoQraftContext.Provider, {
                value: {
                    requestFn: securedRequestFn,
                    baseUrl: apiUrl,
                    queryClient: queryClient
                },
                children: /*#__PURE__*/ jsx(SDKDemoAPIContext.Provider, {
                    value: {
                        api,
                        version,
                        requestFn: securedRequestFn
                    },
                    children: children
                })
            })
    });
};
const SDKDemoQraftContext = /*#__PURE__*/ createContext(undefined);
const SDKDemoAPIContext = /*#__PURE__*/ createContext(undefined);
const useSDKDemoAPI = ()=>{
    const value = useContext(SDKDemoAPIContext);
    if (!value) {
        throw new Error('useSDKDemoAPI must be used within a SDKDemoAPIProvider');
    }
    return value;
};

const DefaultLayout = ({ children, siderProps })=>{
    const location = useLocation();
    const { api } = useSDKDemoAPI();
    const { data: user, isLoading: isUserLoading } = api.entityUsers.getEntityUsersMe.useQuery({});
    useEffect(()=>{
        window.scrollTo(0, 0);
    }, [
        location
    ]);
    const drawerWidth = '240px';
    return /*#__PURE__*/ jsx(Fragment, {
        children: /*#__PURE__*/ jsxs(Box, {
            sx: {
                display: 'flex',
                m: 0,
                height: '100vh',
                width: '100vw'
            },
            children: [
                /*#__PURE__*/ jsxs(Drawer, {
                    sx: {
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box'
                        }
                    },
                    variant: "permanent",
                    anchor: "left",
                    children: [
                        /*#__PURE__*/ jsxs(Box, {
                            sx: {
                                display: 'flex',
                                alignItems: 'center',
                                p: 2
                            },
                            children: [
                                !isUserLoading && user && /*#__PURE__*/ jsxs(Fragment, {
                                    children: [
                                        /*#__PURE__*/ jsx(Avatar, {
                                            sx: {
                                                width: 44,
                                                height: 44
                                            },
                                            alt: user.first_name,
                                            src: user.userpic_file_id || undefined
                                        }),
                                        /*#__PURE__*/ jsxs(Typography, {
                                            ml: 1,
                                            variant: "button",
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            children: [
                                                user.first_name,
                                                " ",
                                                user.last_name
                                            ]
                                        })
                                    ]
                                }),
                                isUserLoading && /*#__PURE__*/ jsx(CircularProgress, {
                                    size: 44
                                })
                            ]
                        }),
                        /*#__PURE__*/ jsx(Box, {
                            display: "flex",
                            sx: {
                                flex: 1
                            },
                            children: /*#__PURE__*/ jsx(Menu, {})
                        }),
                        /*#__PURE__*/ jsx(Box, {
                            children: /*#__PURE__*/ jsx(Stack, {
                                direction: "column",
                                spacing: 2,
                                mx: 2,
                                mb: 2,
                                children: siderProps?.footer
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ jsx(Box, {
                    sx: {
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        height: 'inherit',
                        position: 'relative',
                        width: `calc(100vw - ${drawerWidth})`
                    },
                    children: children
                })
            ]
        })
    });
};

const MONITE_AUTH_CREDENTIALS_LOCAL_STORAGE_KEY = 'MONITE_AUTH_CREDENTIALS';

async function getConfig() {
    const res = await fetch('/config.json', {
        method: 'GET',
        headers: {
            Accept: 'application/json'
        }
    });
    if (!res.ok) {
        throw new Error(`
      Could not load config.json. Check that "config.json" file exists in the "public" folder.
      Please follow the instructions in the README.md file.
      Or execute this code in the console:
      'cp config.json.example public/config.json'
    `);
    }
    return await res.json();
}

async function fetchToken(apiUrl, { entity_user_id, client_id, client_secret }) {
    const res = await fetch(`${apiUrl}/auth/token`, {
        method: 'POST',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            'x-monite-version': '2023-04-12'
        },
        body: JSON.stringify({
            grant_type: 'entity_user',
            entity_user_id,
            client_id,
            client_secret
        })
    });
    if (!res.ok) {
        throw new Error(`Could not fetch token: ${await res.text()}`);
    }
    return await res.json();
}

/*eslint-disable*/ const messages = JSON.parse("{\"b9Y4up\":\"Client ID\",\"Bdj4LI\":\"Client Secret\",\"dU8ZD6\":\"Counterparts\",\"oRVm8M\":\"Dark Mode\",\"cYh1Vn\":\"Entity User ID\",\"ubywYC\":[\"Error: <0>\",[\"0\"],\"</0>\"],\"nbfdhU\":\"Integrations\",\"z0t9bb\":\"Login\",\"nOhz3x\":\"Logout\",\"tcVBwJ\":\"Material UI\",\"GUNeuK\":\"Monite\",\"Q3KR12\":\"Onboard Entity\",\"HjWVjB\":\"Payables\",\"N0qXpE\":\"Products\",\"McU8nI\":\"Roles & Approvals\",\"mUv9U4\":\"Sales\",\"Tz0i8g\":\"Settings\",\"OYHzN1\":\"Tags\",\"FEr96N\":\"Theme\",\"9eF5oV\":\"Welcome back\"}");

const SDKDemoI18nProvider = ({ children, localeCode })=>{
    const sdkDemoI18n = useMemo(()=>{
        return setupI18n({
            locale: localeCode,
            messages: {
                [localeCode]: messages
            }
        });
    }, [
        localeCode
    ]);
    return /*#__PURE__*/ jsx(I18nProvider, {
        i18n: sdkDemoI18n,
        children: children
    });
};

export { DefaultLayout, EntityIdLoader, MONITE_AUTH_CREDENTIALS_LOCAL_STORAGE_KEY, SDKDemoAPIProvider, SDKDemoI18nProvider, createEntityUsersMyEntityRequestFn, fetchToken, getConfig };
//# sourceMappingURL=index.js.map
